import isURL from 'validator/es/lib/isURL';
import { translatedError, alphaNumUnderscore, startWithNumber } from '@/utils/errors';
import { assignValue, hasMinimumLength } from '@/utils/resultsHelper';
import fieldNames, { dictionaryEntityFieldTypes, globalFieldLengths } from '@/constants/fields';
import { ruleToFieldProps, validRuleFromFunction } from '../pmn-functions/helper';

const validateDictionaryField = (
  {
    description,
    label,
    name,
    type,
    uri,
    uuid,
  },
  errors,
  {
    dictionaryName,
    dictionaryUUID,
  },
  duplicates,
) => {
  const context = {
    entity: 'dictionary', dictionaryUUID, dictionaryName, fieldUUID: uuid,
  };
  if (!hasMinimumLength(name)) {
    const error = translatedError(
      context,
      'save.dataset.validation.required.dictionary.field.name.title',
      'save.dataset.validation.required.dictionary.field.name',
      [fieldNames.NAME],
    );
    error.translation.titleArgs = { name: dictionaryName };
    errors.push(error);
  }

  if (name && name.length > globalFieldLengths.dictionaryFieldName) {
    const error = translatedError(
      context,
      'save.dataset.validation.dictionary.field.name.title',
      'validation.max.length',
      [fieldNames.NAME],
      { maximum: globalFieldLengths.name },
    );
    error.translation.titleArgs = { name: dictionaryName };
    errors.push(error);
  }

  if (name && duplicates.includes(name.toLowerCase())) {
    const error = translatedError(
      context,
      'save.dataset.validation.dictionary.field.name.title',
      'save.dataset.validation.field.not.unique',
      [fieldNames.NAME],
    );
    error.translation.titleArgs = { name: dictionaryName };
    errors.push(error);
  }

  if (name && !alphaNumUnderscore(name)) {
    const error = translatedError(
      context,
      'save.dataset.validation.dictionary.field.name.title',
      'save.dataset.validation.field.not.alphanumeric.underscore',
      [fieldNames.NAME],
    );
    error.translation.titleArgs = { name: dictionaryName };
    errors.push(error);
  }

  if (name && startWithNumber(name)) {
    const error = translatedError(
      context,
      'save.dataset.validation.dictionary.field.name.title',
      'save.dataset.validation.field.not.start.with.number',
      [fieldNames.NAME],
    );
    error.translation.titleArgs = { name: dictionaryName };
    errors.push(error);
  }

  if (!hasMinimumLength(label)) {
    const error = translatedError(
      context,
      'save.dataset.validation.required.dictionary.field.label.title',
      'save.dataset.validation.required.dictionary.field.label',
      [fieldNames.LABEL],
    );
    error.translation.titleArgs = { name: dictionaryName };
    errors.push(error);
  }

  if (label && label.length > globalFieldLengths.dictionaryFieldLabel) {
    const error = translatedError(
      context,
      'save.dataset.validation.dictionary.field.label.title',
      'validation.max.length',
      [fieldNames.LABEL],
      { maximum: globalFieldLengths.name },
    );
    error.translation.titleArgs = { name: dictionaryName };
    errors.push(error);
  }

  if (type === null) {
    const error = translatedError(
      context,
      'save.dataset.validation.required.dictionary.field.type.title',
      'save.dataset.validation.required.dictionary.field.type',
      [fieldNames.TYPE],
    );
    error.translation.titleArgs = { name: dictionaryName };
    errors.push(error);
  }

  if (description && description.length > globalFieldLengths.description) {
    const error = translatedError(
      context,
      'save.dataset.validation.dictionary.field.description.title',
      'validation.max.length',
      [fieldNames.DESCRIPTION],
      { maximum: globalFieldLengths.description },
    );
    error.translation.titleArgs = { name: dictionaryName };
    errors.push(error);
  }

  if (uri && uri.length > globalFieldLengths.url) {
    const error = translatedError(
      context,
      'save.dataset.validation.dictionary.field.uri.title',
      'validation.max.length',
      [fieldNames.URI],
      { maximum: globalFieldLengths.url },
    );
    error.translation.titleArgs = { name: dictionaryName };
    errors.push(error);
  }

  if (uri && !isURL(uri)) {
    const error = translatedError(
      context,
      'save.dataset.validation.dictionary.field.uri.title',
      'validation.uri.not.valid',
      [fieldNames.URI],
    );
    error.translation.titleArgs = { name: dictionaryName };
    errors.push(error);
  }
};

const validateDictionaryFields = ({ fields, name, uuid }, errors) => {
  const names = fields.map((field) => field.name && field.name.toLowerCase());
  const duplicates = names.filter((n, index) => names.indexOf(n) !== index);

  if (fields.length === 0) {
    const error = translatedError(
      { entity: 'dictionary', dictionaryUUID: uuid, dictionaryName: name },
      'save.dataset.validation.required.dictionary.fields.title',
      'save.dataset.validation.required.dictionary.fields',
      [fieldNames.FIELD],
    );
    error.translation.titleArgs = { name };
    errors.push(error);
  }

  fields.forEach((field) => {
    validateDictionaryField(
      field,
      errors,
      { dictionaryUUID: uuid, dictionaryName: name },
      duplicates,
    );
  });
};

const updateEntityByType = (field) => {
  if (!Object.values(dictionaryEntityFieldTypes).includes(field.type)) {
    assignValue(field, 'entity', false);
  }
};

const updatePmnRuleByType = (field, pmnFunctions) => {
  const pmnRule = validRuleFromFunction(field.pseudonymisation_rule, field.type, pmnFunctions);
  assignValue(field, 'pseudonymisation_rule', ruleToFieldProps(pmnRule));
};

export const cascadeFieldTypeUpdate = (field, pmnFunctions = []) => {
  updateEntityByType(field);
  if (pmnFunctions?.length) {
    updatePmnRuleByType(field, pmnFunctions);
  }
};

export default validateDictionaryFields;
